"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Day() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: closingRef,
    isVisible: closingVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const acts = [{
    number: "STEP 01",
    time: "08:00 — ARRIVAL",
    title: "THE MORNING WELCOME",
    description: "Children are greeted at the door by our educators, transitioning from the bustle of the morning into the calm, prepared environment of our classroom. Shoes are stored, and the day begins with a gentle, independent start.",
    duration: "30 MINUTES",
    interlude: "TRANSITION — 0 MIN"
  }, {
    number: "STEP 02",
    time: "08:30 — WORK CYCLE",
    title: "THE MORNING WORK CYCLE",
    description: "A period of uninterrupted, deep engagement. Children choose activities from our five curriculum areas, developing concentration, fine motor skills, and a sense of order through purposeful, hands-on exploration.",
    duration: "180 MINUTES",
    interlude: "INTERMISSION — 15 MIN"
  }, {
    number: "STEP 03",
    time: "11:30 — CIRCLE TIME",
    title: "THE COMMUNITY GATHERING",
    description: "We come together as a group to share stories, songs, and lessons. This time fosters social connection, language development, and a sense of belonging within our Parkwoods-Donalda community.",
    duration: "30 MINUTES",
    interlude: "PROCESSION — 10 MIN"
  }, {
    number: "STEP 04",
    time: "12:00 — LUNCH & PLAY",
    title: "THE NOURISHMENT",
    description: "Children enjoy a nutritious, communal lunch followed by outdoor play in our fenced playground. Fresh air and physical movement are essential components of our holistic approach to early childhood development.",
    duration: "90 MINUTES",
    interlude: "INTERMISSION — 20 MIN"
  }, {
    number: "STEP 05",
    time: "13:50 — AFTERNOON CYCLE",
    title: "THE AFTERNOON EXPLORATION",
    description: "A second, shorter work cycle allows for continued discovery or specialized group activities. Children refine their skills and engage in collaborative projects that build confidence and curiosity.",
    duration: "100 MINUTES",
    interlude: "EPILOGUE — 0 MIN"
  }, {
    number: "STEP 06",
    time: "15:30 — DEPARTURE",
    title: "THE FAREWELL",
    description: "As the day concludes, children tidy their workspace, reflecting on their accomplishments. We share a final moment of connection before families reunite, carrying the day's learning home.",
    duration: "30 MINUTES",
    interlude: null
  }];
  return <section data-section-id="4131" id="a-day-at-little-acorns" className="bg-background text-foreground pt-[200px] pb-[160px] px-6 md:px-12 lg:px-20 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {}
        <div ref={headerRef} className={`transition-all duration-1000 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="font-mono text-xs md:text-sm tracking-widest mb-10 md:mb-16 flex items-center gap-3">
            <span className="inline-block w-8 h-px bg-primary" />
            <span className="text-primary">
              OUR PROGRAM — 01 / A DAY AT LITTLE ACORNS
            </span>
          </div>

          <h1 className="font-default font-bold leading-[0.95] tracking-tighter mb-12 md:mb-16" style={{
          fontSize: "clamp(64px, 10vw, 160px)"
        }}>
            <span className="block">A DAY OF</span>
            <span className="block">
              GROWTH <span className="font-serif italic font-light">IN SIX STEPS.</span>
            </span>
          </h1>

          <div className="md:w-3/5">
            <p className="font-default text-xl md:text-2xl leading-relaxed text-foreground/80">
              Our daily rhythm is carefully choreographed to balance deep, independent work with meaningful social connection, ensuring every child feels supported in their natural development.
            </p>
          </div>
        </div>

        {}
        <div className="mt-24 md:mt-32 relative">
          {}
          <div className="absolute left-3 md:left-[40%] top-0 bottom-0 w-[2px] bg-primary/40" aria-hidden="true" />

          <div className="space-y-16 md:space-y-24">
            {acts.map((act, i) => {
            return <ActCard key={i} data-index={i} index={i} number={act.number} time={act.time} title={act.title} description={act.description} duration={act.duration} interlude={act.interlude} isLast={i === acts.length - 1} />;
          })}
          </div>
        </div>

        {}
        <div ref={closingRef} className={`mt-24 md:mt-32 text-center transition-all duration-1000 ${closingVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="font-serif italic text-2xl md:text-4xl text-foreground/80 mb-12 md:mb-16">
            The mighty oak from the little acorn grows.
          </p>

          <Link to="/ContactUsPage">
            <Button variant="outline" size="lg" className="bg-primary text-primary-foreground rounded-lg font-default tracking-widest text-sm h-14 px-10 shadow-lg group">
              BOOK A TOUR
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}
function ActCard({
  index,
  number,
  time,
  title,
  description,
  duration,
  interlude,
  isLast,
  ...rest
}: {
  index: number;
  number: string;
  time: string;
  title: string;
  description: string;
  duration: string;
  interlude: string | null;
  isLast: boolean;
  [key: string]: unknown;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const isEven = index % 2 === 1;
  return <div ref={ref} className={`transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} {...rest}>
      <div className="relative flex items-start">
        {}
        <div className="absolute left-0 md:left-[40%] -translate-x-[11px] md:-translate-x-[11px] top-6 w-6 h-6 bg-primary rounded-lg group/marker" aria-hidden="true" />

        {}
        <div className={`ml-12 md:ml-0 w-full md:w-[55%] ${isEven ? "md:ml-auto md:pl-20" : "md:ml-[45%] md:pl-12"}`}>
          <div className="group relative bg-card rounded-3xl border border-border p-8 md:p-10 shadow-md hover:shadow-lg transition-all duration-500 hover:-translate-y-1">
            {}
            <div className="font-mono text-[11px] md:text-xs tracking-widest text-primary mb-6 flex items-center gap-2">
              <span className="inline-block w-4 h-px bg-primary" />
              {number} / {time}
            </div>

            {}
            <h3 className="font-default font-bold text-2xl md:text-[28px] leading-tight tracking-tight mb-5 text-foreground">
              {title}
            </h3>

            {}
            <p className="font-default text-base md:text-lg leading-relaxed text-foreground/70 mb-8">
              {description}
            </p>

            {}
            <div className="pt-6 border-t border-border/60 font-mono text-[11px] tracking-widest text-muted-foreground">
              {duration}
            </div>
          </div>
        </div>
      </div>

      {}
      {!isLast && interlude && <div className="relative mt-12 md:mt-16">
          <div className="absolute left-3 md:left-[40%] -translate-x-1/2 w-2 h-2 bg-primary/60 rounded-lg" aria-hidden="true" />
          <div className={`ml-12 md:ml-0 ${!isEven ? "md:ml-auto md:pl-20 md:w-[55%]" : "md:ml-[45%] md:pl-12 md:w-[55%]"}`}>
            <span className="font-mono text-[10px] md:text-xs tracking-widest text-muted-foreground/70">
              — {interlude}
            </span>
          </div>
        </div>}
    </div>;
}